import React, { useState, useEffect } from 'react';
import { Phone, Send, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ruonn-replica/artifacts/652j8z6m_logo%20letra%20transparente%20white.png';

const BookingForm = ({ isModal = false, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    message: '',
    securityCode: '',
  });
  const [generatedCode, setGeneratedCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    generateSecurityCode();
  }, []);

  const generateSecurityCode = () => {
    const code = Math.floor(1000 + Math.random() * 9000).toString();
    setGeneratedCode(code);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate security code
    if (formData.securityCode !== generatedCode) {
      setError('Invalid security code. Please try again.');
      generateSecurityCode();
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        company: formData.company,
        message: formData.message,
      });
      
      setIsSubmitted(true);
      setTimeout(() => {
        if (onClose) onClose();
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className={`${isModal ? '' : 'bg-[#1a1a1f] rounded-3xl border border-white/10 p-8'}`}>
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h3 className="text-2xl text-white font-medium mb-3">Thank You!</h3>
          <p className="text-gray-400">We've received your request. Our team will contact you within 24 hours.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isModal ? '' : 'bg-[#1a1a1f] rounded-3xl border border-white/10 p-8'}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <img 
            src={LOGO_URL} 
            alt="Ruonn" 
            className="h-6 w-auto"
          />
        </div>
        <h3 className="text-2xl text-white font-medium mb-2">Let's Build Your Dream Team</h3>
        <p className="text-gray-400 text-sm">Fill out the form and we'll get back to you within 24 hours.</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-sm mb-2">First Name</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
              placeholder="John"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">Last Name</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
              placeholder="Doe"
            />
          </div>
        </div>

        <div>
          <label className="block text-gray-400 text-sm mb-2">Work Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
            placeholder="john@company.com"
          />
        </div>

        <div>
          <label className="block text-gray-400 text-sm mb-2">Company</label>
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
            placeholder="Your Company"
          />
        </div>

        <div>
          <label className="block text-gray-400 text-sm mb-2">Message</label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={3}
            className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors resize-none"
            placeholder="Tell us about your project and team needs..."
          />
        </div>

        <div>
          <label className="block text-gray-400 text-sm mb-2">Security Code</label>
          <div className="flex gap-4 items-center">
            <div className="px-4 py-3 bg-purple-600/20 rounded-xl text-purple-400 font-mono text-lg tracking-widest">
              {generatedCode}
            </div>
            <input
              type="text"
              name="securityCode"
              value={formData.securityCode}
              onChange={handleChange}
              required
              maxLength={4}
              className="flex-1 px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
              placeholder="Enter code"
            />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-400 text-sm p-3 bg-red-500/10 rounded-xl">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 bg-[#7c3aed] hover:bg-[#6d28d9] disabled:bg-gray-600 text-white px-6 py-4 rounded-xl font-medium transition-all duration-300 hover:scale-[1.02] disabled:scale-100"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Phone className="w-5 h-5" />
              Book a Call
            </>
          )}
        </button>

        <p className="text-gray-500 text-xs text-center">
          By submitting this form, you agree to our{' '}
          <a href="#" className="text-purple-400 hover:underline">Privacy Policy</a>
        </p>
      </form>
    </div>
  );
};

export default BookingForm;
